import {
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
  type Accessor,
} from 'solid-js';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import type { FxWorkspaceRepository } from './fx-workspace-repository';
import type { FxWorkspace, FxWorkspaceLoadStatus } from './fx-workspace.types';
import {
  FX_WORKSPACE_REFERENCES_QUERY_KEY,
  fxWorkspaceReferencesQueryKey,
  toQueryLoadStatus,
  type FxWorkspaceDetailsKey,
} from './fx-workspace-query';
import {
  createRepositoryScopeGuard,
  createWorkspaceLoadTokens,
  createWorkspaceScopeGuard,
} from './fx-workspace-scope';

export interface FxWorkspaceReferencesApi {
  readonly loadStatus: Accessor<FxWorkspaceLoadStatus>;
  loadWorkspaceReferences(workspaceId: string): Promise<void>;
  isReferenceVisible(workspaceId: string, refTrn: string): boolean;
  toggleReferenceVisibility(workspaceId: string, refTrn: string): void;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

interface PendingReferenceDiff {
  readonly added: Set<string>;
  readonly removed: Set<string>;
}

function mergeServerReferencesWithPending(
  serverRefs: readonly string[],
  pending: PendingReferenceDiff | undefined
): Set<string> {
  const merged = new Set(serverRefs);
  if (pending === undefined) {
    return merged;
  }

  for (const refTrn of pending.removed) {
    merged.delete(refTrn);
  }
  for (const refTrn of pending.added) {
    merged.add(refTrn);
  }
  return merged;
}

export function useFxWorkspaceReferences(options: {
  repository: Accessor<FxWorkspaceRepository>;
  fetchKey: Accessor<string | undefined>;
  workspaces: Accessor<readonly FxWorkspace[]>;
  activeWorkspaceId: Accessor<string | undefined>;
  detailsKey: Accessor<FxWorkspaceDetailsKey | undefined>;
}): FxWorkspaceReferencesApi {
  const logger = useLogger().getLogger('FxWorkspace');
  const queryClient = useQueryClient();

  const [workspaceReferences, setWorkspaceReferences] = createSignal<
    Record<string, ReadonlySet<string>>
  >({});
  const [loadedReferenceWorkspaceIds, setLoadedReferenceWorkspaceIds] =
    createSignal<ReadonlySet<string>>(new Set());
  const scope = createRepositoryScopeGuard();
  const workspaceScope = createWorkspaceScopeGuard();
  const referenceLoadTokens = createWorkspaceLoadTokens();
  const referenceToggleTokens = createWorkspaceLoadTokens();
  const referencePersistChains = new Map<string, Promise<void>>();
  const pendingReferenceDiffs = new Map<string, PendingReferenceDiff>();

  const referenceToggleTokenKey = (
    workspaceId: string,
    refTrn: string
  ): string => `${workspaceId}\0${refTrn}`;

  const enqueueReferencePersist = (
    key: string,
    persist: () => Promise<void>
  ): void => {
    const previous = referencePersistChains.get(key) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(persist)
      .catch(() => undefined);
    referencePersistChains.set(key, next);
    void next.finally(() => {
      if (referencePersistChains.get(key) === next) {
        referencePersistChains.delete(key);
      }
    });
  };

  const setWorkspaceReferenceSet = (
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void => {
    setWorkspaceReferences((previous) => ({
      ...previous,
      [workspaceId]: refTrns,
    }));
  };

  const cacheWorkspaceReferences = (
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void => {
    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    queryClient.setQueryData(fxWorkspaceReferencesQueryKey(key, workspaceId), [
      ...refTrns,
    ]);
  };

  const getPendingReferenceDiff = (
    workspaceId: string
  ): PendingReferenceDiff => {
    const existing = pendingReferenceDiffs.get(workspaceId);
    if (existing !== undefined) {
      return existing;
    }

    const created: PendingReferenceDiff = {
      added: new Set<string>(),
      removed: new Set<string>(),
    };
    pendingReferenceDiffs.set(workspaceId, created);
    return created;
  };

  const recordPendingToggle = (
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void => {
    const diff = getPendingReferenceDiff(workspaceId);
    if (wasVisible) {
      diff.added.delete(refTrn);
      diff.removed.add(refTrn);
      return;
    }

    diff.removed.delete(refTrn);
    diff.added.add(refTrn);
  };

  const revertPendingToggle = (
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void => {
    const diff = pendingReferenceDiffs.get(workspaceId);
    if (diff === undefined) {
      return;
    }

    if (wasVisible) {
      diff.removed.delete(refTrn);
      diff.added.add(refTrn);
      return;
    }

    diff.added.delete(refTrn);
  };

  const applyWorkspaceReferences = (
    workspaceId: string,
    refTrns: readonly string[]
  ): void => {
    if (loadedReferenceWorkspaceIds().has(workspaceId)) {
      return;
    }

    const merged = mergeServerReferencesWithPending(
      refTrns,
      pendingReferenceDiffs.get(workspaceId)
    );
    pendingReferenceDiffs.delete(workspaceId);
    setWorkspaceReferenceSet(workspaceId, merged);
    setLoadedReferenceWorkspaceIds(
      (previous) => new Set<string>([...previous, workspaceId])
    );
    cacheWorkspaceReferences(workspaceId, merged);
  };

  const referencesQuery = useQuery(() => {
    const detailsKey = options.detailsKey();
    const enabled = detailsKey !== undefined;

    return {
      enabled,
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
      queryKey:
        detailsKey === undefined
          ? ([FX_WORKSPACE_REFERENCES_QUERY_KEY, 'disabled'] as const)
          : fxWorkspaceReferencesQueryKey(
              detailsKey.key,
              detailsKey.workspaceId
            ),
      queryFn: () => {
        const workspaceId = detailsKey?.workspaceId;
        if (workspaceId === undefined) {
          return Promise.resolve([] as readonly string[]);
        }

        return untrack(options.repository).listReferences(workspaceId);
      },
      retry: false,
    };
  });

  const loadStatus = createMemo(
    (): FxWorkspaceLoadStatus => toQueryLoadStatus(referencesQuery.status)
  );

  createEffect(
    on(
      () => ({
        workspaceId: options.detailsKey()?.workspaceId,
        data: referencesQuery.data,
      }),
      (snapshot) => {
        if (snapshot.workspaceId === undefined || snapshot.data === undefined) {
          return;
        }

        if (loadedReferenceWorkspaceIds().has(snapshot.workspaceId)) {
          return;
        }

        applyWorkspaceReferences(snapshot.workspaceId, snapshot.data);
      }
    )
  );

  createEffect(
    on(
      () => (referencesQuery.isError ? referencesQuery.error : undefined),
      (error, previousError) => {
        if (error === undefined || error === null || error === previousError) {
          return;
        }

        logger.error(
          'Failed to load FX workspace references',
          { workspaceId: options.activeWorkspaceId() },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.loadFailed'),
        });
      }
    )
  );

  const loadWorkspaceReferences = async (
    workspaceId: string
  ): Promise<void> => {
    if (loadedReferenceWorkspaceIds().has(workspaceId)) {
      return;
    }

    const workspaceExists = options
      .workspaces()
      .some((workspace) => workspace.id === workspaceId);
    if (!workspaceExists) {
      return;
    }

    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    const loadToken = referenceLoadTokens.bump(workspaceId);
    const scopeSnapshot = scope.capture();

    try {
      const refTrns = await queryClient.fetchQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey + workspaceId
        queryKey: fxWorkspaceReferencesQueryKey(key, workspaceId),
        queryFn: () => untrack(options.repository).listReferences(workspaceId),
      });
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !referenceLoadTokens.isLatest(workspaceId, loadToken)
      ) {
        return;
      }

      applyWorkspaceReferences(workspaceId, refTrns);
    } catch (error: unknown) {
      if (
        !scope.isCurrent(scopeSnapshot) ||
        !referenceLoadTokens.isLatest(workspaceId, loadToken)
      ) {
        return;
      }

      logger.error(
        'Failed to load FX workspace references',
        { workspaceId },
        asError(error)
      );
      if (options.activeWorkspaceId() !== workspaceId) {
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.loadFailed'),
        });
      }
    }
  };

  const isReferenceVisible = (workspaceId: string, refTrn: string): boolean => {
    return workspaceReferences()[workspaceId]?.has(refTrn) ?? false;
  };

  const toggleReferenceVisibility = (
    workspaceId: string,
    refTrn: string
  ): void => {
    const scopeSnapshot = scope.capture();
    const workspaceSnapshot = workspaceScope.capture(workspaceId);
    const key = options.fetchKey();
    const isHydrated = loadedReferenceWorkspaceIds().has(workspaceId);

    if (isHydrated && key !== undefined) {
      void queryClient.cancelQueries({
        queryKey: fxWorkspaceReferencesQueryKey(key, workspaceId),
      });
    }

    const previousRefs =
      workspaceReferences()[workspaceId] ?? new Set<string>();
    const isVisible = previousRefs.has(refTrn);
    const toggleTokenKey = referenceToggleTokenKey(workspaceId, refTrn);
    const toggleToken = referenceToggleTokens.bump(toggleTokenKey);
    const nextRefs = new Set(previousRefs);

    if (isVisible) {
      nextRefs.delete(refTrn);
    } else {
      nextRefs.add(refTrn);
    }

    setWorkspaceReferenceSet(workspaceId, nextRefs);
    if (isHydrated) {
      cacheWorkspaceReferences(workspaceId, nextRefs);
    } else {
      recordPendingToggle(workspaceId, refTrn, isVisible);
    }

    const activeRepository = options.repository();
    const isPersistCurrent = (): boolean =>
      scope.isCurrent(scopeSnapshot) &&
      workspaceScope.isCurrent(workspaceId, workspaceSnapshot) &&
      referenceToggleTokens.isLatest(toggleTokenKey, toggleToken);

    const persistToggle = async (): Promise<void> => {
      if (!isPersistCurrent()) {
        return;
      }

      try {
        const toAdd = isVisible ? [] : [refTrn];
        const toRemove = isVisible ? [refTrn] : [];

        await activeRepository.syncReferences(workspaceId, { toAdd, toRemove });
      } catch (error: unknown) {
        if (!isPersistCurrent()) {
          return;
        }

        logger.error(
          'Failed to update FX workspace reference',
          { workspaceId, refTrn, isVisible },
          asError(error)
        );

        const currentRefs =
          workspaceReferences()[workspaceId] ?? new Set<string>();
        const rolledBack = new Set(currentRefs);
        if (isVisible) {
          rolledBack.add(refTrn);
        } else {
          rolledBack.delete(refTrn);
        }

        setWorkspaceReferenceSet(workspaceId, rolledBack);
        if (loadedReferenceWorkspaceIds().has(workspaceId)) {
          cacheWorkspaceReferences(workspaceId, rolledBack);
        } else {
          revertPendingToggle(workspaceId, refTrn, isVisible);
        }
        NotificationEventBus.emit({
          type: 'error',
          message: t('mobileMapping.fxWorkspace.references.toggleFailed'),
        });
      }
    };

    enqueueReferencePersist(toggleTokenKey, persistToggle);
  };

  const clearWorkspace = (workspaceId: string): void => {
    workspaceScope.invalidate(workspaceId);
    setWorkspaceReferences((previous) => {
      const next = { ...previous };
      delete next[workspaceId];
      return next;
    });
    setLoadedReferenceWorkspaceIds((previous) => {
      const next = new Set(previous);
      next.delete(workspaceId);
      return next;
    });
    pendingReferenceDiffs.delete(workspaceId);
    referenceLoadTokens.delete(workspaceId);
    const repositoryKey = options.fetchKey();
    if (repositoryKey !== undefined) {
      queryClient.removeQueries({
        queryKey: fxWorkspaceReferencesQueryKey(repositoryKey, workspaceId),
      });
    }
  };

  return {
    loadStatus,
    loadWorkspaceReferences,
    isReferenceVisible,
    toggleReferenceVisibility,
    clearWorkspace,
    resetRepositoryScope: (): void => {
      scope.invalidate();
      workspaceScope.clear();
      referenceLoadTokens.clear();
      referenceToggleTokens.clear();
      referencePersistChains.clear();
      pendingReferenceDiffs.clear();
      setWorkspaceReferences({});
      setLoadedReferenceWorkspaceIds(new Set<string>());
    },
  };
}
